import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Sprout, Warehouse, ArrowRight, Store, PackageCheck } from "lucide-react";
import api from "../../lib/api";
import { Card, Badge, Button, PageLoader } from "../../lib/ui";
import { fmtRp } from "../../lib/constants";

function SummaryCard({ icon: Icon, label, value, sub, tone = "primary", testid }) {
  const tones = { primary: "bg-primary-light text-primary", green: "bg-emerald-50 text-emerald-600", blue: "bg-blue-50 text-blue-600", amber: "bg-amber-50 text-amber-600" };
  return (
    <Card className="flex items-center gap-3 p-4" data-testid={testid}>
      <span className={`flex h-11 w-11 items-center justify-center rounded-2xl ${tones[tone]}`}><Icon size={20} /></span>
      <div className="min-w-0">
        <p className="text-xs font-bold text-ink-mute">{label}</p>
        <p className="font-heading text-xl font-extrabold leading-tight text-ink">{value}</p>
        {sub && <p className="text-[11px] text-ink-mute">{sub}</p>}
      </div>
    </Card>
  );
}

function DemandQueue({ title, icon: Icon, rows, accent, onExecute, testid }) {
  return (
    <Card className="overflow-hidden p-0" data-testid={testid}>
      <div className={`flex items-center gap-2 border-b border-line px-5 py-4 ${accent}`}>
        <Icon size={18} />
        <h3 className="font-heading text-base font-extrabold">{title}</h3>
        <Badge tone="mute" className="ml-auto">{rows.length} item</Badge>
      </div>
      {rows.length === 0 ? (
        <p className="px-5 py-8 text-center text-sm text-ink-mute">Tidak ada permintaan pada antrean ini hari ini</p>
      ) : (
        <div className="divide-y divide-line">
          {rows.map((r) => (
            <div key={r.product_id} className="px-5 py-3" data-testid={`pw-row-${r.product_id}`}>
              <div className="flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate font-bold text-ink">{r.name}</p>
                  <div className="mt-0.5 flex flex-wrap gap-1">
                    {r.branches.map((b) => (
                      <span key={b.branch_name} className="rounded-full bg-app px-2 py-0.5 text-[10px] font-semibold text-ink-soft">
                        {b.branch_name}: {b.qty}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <p className="font-heading text-lg font-extrabold text-ink">{r.total_qty} <span className="text-xs font-semibold text-ink-mute">{r.unit}</span></p>
                  <p className="text-[11px] text-ink-mute">~{fmtRp(r.est_value)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="border-t border-line p-4">
        <Button variant="outline" className="w-full" onClick={onExecute} data-testid={`${testid}-execute`}>
          Eksekusi di Purchase Order <ArrowRight size={15} />
        </Button>
      </div>
    </Card>
  );
}

export default function BranchDemandTab() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/workspace/purchasing"); setData(data); } catch (e) { /* */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <PageLoader />;
  const s = data.summary;

  return (
    <div data-testid="sc-branch-demand">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <SummaryCard icon={Store} label="Cabang Mengirim" value={s.branches_submitted} sub={`${s.orders_submitted} pesanan`} tone="primary" testid="pw-sum-branches" />
        <SummaryCard icon={Sprout} label="Fresh Market" value={s.fresh_count} sub={`~${fmtRp(s.fresh_value)}`} tone="green" testid="pw-sum-fresh" />
        <SummaryCard icon={Warehouse} label="Pengadaan Pusat" value={s.procurement_count} sub={`~${fmtRp(s.procurement_value)}`} tone="blue" testid="pw-sum-proc" />
        <SummaryCard icon={PackageCheck} label="Dibeli Hari Ini" value={s.orders_purchased} sub={`${s.orders_approved} disetujui`} tone="amber" testid="pw-sum-purchased" />
      </div>
      <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-2">
        <DemandQueue title="Antrean Fresh Market" icon={Sprout} rows={data.fresh_market} accent="bg-emerald-50 text-emerald-700"
          onExecute={() => navigate("/procurement")} testid="pw-fresh-queue" />
        <DemandQueue title="Antrean Pengadaan Pusat" icon={Warehouse} rows={data.procurement} accent="bg-blue-50 text-blue-700"
          onExecute={() => navigate("/procurement")} testid="pw-proc-queue" />
      </div>
    </div>
  );
}
